import { DateTime } from "luxon";
import type { CensusPointInTimes } from "../../utils/models";
import { formatCensus } from "../dailyPrintout/util/censusPointInTimes";
import { formatTimeTo12Hour } from "../dailyPrintout/util/shiftTimeFormat";
import { type AiPrintoutOrders, rankIn, textOrNull } from "./aiPrintoutRowMappers";
import type { AiPrintoutCensus, AiPrintoutCensusReading } from "./contract";

/** The day's census as the Day view holds it (`DayStats.census`): 0 means none is entered, as the HPPD math reads it. */
export const censusTotalOf = (dayCensus: number): number | null => (dayCensus > 0 ? dayCensus : null);

/** A fixed date with no DST change, so an hour of the day always reads as itself (as `customTimeGroups` anchors its times). */
const CLOCK_ANCHOR = { year: 2000, month: 1, day: 1 } as const;

const readingsOf = (readings: CensusPointInTimes | null): AiPrintoutCensusReading[] | null => {
	// The Detailed header prints nothing for a day with no readings, the same rule `censusPointInTimeRows` keeps.
	if (readings === null || !readings.available || readings.points.length === 0) return null;
	return readings.points.map((point) => ({
		hour: point.hour,
		timeLabel: formatTimeTo12Hour(DateTime.fromObject({ ...CLOCK_ANCHOR, hour: point.hour }, { zone: "UTC" }), undefined),
		census: formatCensus(point.census),
	}));
};

interface BuildCensusArgs {
	dayCensus: number;
	/** The Detailed source rows' location census (`buildLocationCensusByName`), empty when the day has none by location. */
	censusByLocationName: Map<string, number>;
	readings: CensusPointInTimes | null;
	orders: AiPrintoutOrders;
}

export const buildCensus = ({ dayCensus, censusByLocationName, readings, orders }: BuildCensusArgs): AiPrintoutCensus => {
	const points = readingsOf(readings);
	// A position saved with no location name has no name to print the census after, nor a place in the location order.
	const named = [...censusByLocationName].filter(([location]) => textOrNull(location) !== null);
	return {
		total: censusTotalOf(dayCensus),
		byLocation: named.length === 0 ? null : named.map(([location, census]) => ({ location, locationRank: rankIn(orders.locationRanks, location), census })),
		readings: points,
		readingsAverage: points === null || readings === null || readings.average === null ? null : formatCensus(readings.average),
	};
};
